import { AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui';
import { httpClient } from '../../core/http';
import { fireSuccessConfetti } from '../../utils/confetti';

type VerificationStatus = 'pending' | 'success' | 'error';

const VERIFY_EMAIL_ENDPOINT = '/v1/users/verify-email';
const REDIRECT_TO_LOGIN_DELAY_MS = 3000;

interface VerificationResult {
  nextStatus: VerificationStatus;
  message: string;
}

const VerifyEmail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token');
  const queryStatus = searchParams.get('status');
  const queryMessage = searchParams.get('message');

  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus>('pending');
  const [message, setMessage] = useState<string>(t('auth.verifyEmail.pendingTitle'));
  const hasVerifiedRef = useRef<boolean>(false);

  const extractErrorMessage = useCallback(
    (error: unknown, fallback: string): string => {
      const detail = (error as { data?: { detail?: unknown } })?.data?.detail;
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
      const directMessage = (error as { message?: unknown })?.message;
      if (typeof directMessage === 'string' && directMessage.trim()) {
        return directMessage;
      }
      return fallback;
    },
    []
  );

  const verifyTokenAgainstApi = useCallback(
    async (verificationToken: string): Promise<VerificationResult> => {
      try {
        await httpClient.get(VERIFY_EMAIL_ENDPOINT, {
          params: { token: verificationToken },
        });
        return {
          nextStatus: 'success',
          message: t('auth.verifyEmail.successDescription'),
        };
      } catch (error) {
        return {
          nextStatus: 'error',
          message: extractErrorMessage(error, t('auth.verifyEmail.errorDescription')),
        };
      }
    },
    [extractErrorMessage, t]
  );

  useEffect(() => {
    if (hasVerifiedRef.current) {
      return;
    }

    const resolveLegacyQuery = (): VerificationResult | null => {
      if (queryStatus === 'success') {
        return {
          nextStatus: 'success',
          message: queryMessage?.trim() || t('auth.verifyEmail.successDescription'),
        };
      }
      if (queryStatus === 'error') {
        return {
          nextStatus: 'error',
          message: queryMessage?.trim() || t('auth.verifyEmail.errorDescription'),
        };
      }
      return null;
    };

    const legacyResult = resolveLegacyQuery();
    if (legacyResult) {
      hasVerifiedRef.current = true;
      setVerificationStatus(legacyResult.nextStatus);
      setMessage(legacyResult.message);
      return;
    }

    if (!token) {
      hasVerifiedRef.current = true;
      setVerificationStatus('error');
      setMessage(t('auth.verifyEmail.tokenMissingDescription'));
      return;
    }

    hasVerifiedRef.current = true;
    setVerificationStatus('pending');
    setMessage(t('auth.verifyEmail.pendingDescription'));

    void verifyTokenAgainstApi(token).then(result => {
      setVerificationStatus(result.nextStatus);
      setMessage(result.message);
    });
  }, [queryMessage, queryStatus, t, token, verifyTokenAgainstApi]);

  useEffect(() => {
    if (verificationStatus !== 'success') {
      return;
    }

    fireSuccessConfetti();

    const redirectTimeout = window.setTimeout(() => {
      navigate('/login');
    }, REDIRECT_TO_LOGIN_DELAY_MS);

    return () => window.clearTimeout(redirectTimeout);
  }, [navigate, verificationStatus]);

  const renderStatusIcon = (): React.ReactNode => {
    if (verificationStatus === 'success') {
      return <CheckCircle className="h-16 w-16 text-green-500" />;
    }
    if (verificationStatus === 'error') {
      return <AlertCircle className="h-16 w-16 text-red-500" />;
    }
    return <RefreshCw className="h-16 w-16 text-blue-500 animate-spin" />;
  };

  const titleByStatus: Record<VerificationStatus, string> = {
    success: t('auth.verifyEmail.successTitle'),
    error: t('auth.verifyEmail.errorTitle'),
    pending: t('auth.verifyEmail.pendingTitle'),
  };

  const messageColor: Record<VerificationStatus, string> = {
    success: 'text-green-600 dark:text-green-400',
    error: 'text-red-600 dark:text-red-400',
    pending: 'text-blue-600 dark:text-blue-400',
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center">
            {renderStatusIcon()}
          </div>

          <h2 className="mt-6 text-3xl font-extrabold text-gray-900 dark:text-white">
            {titleByStatus[verificationStatus]}
          </h2>

          <p className={`mt-2 text-sm ${messageColor[verificationStatus]}`}>{message}</p>
        </div>

        {verificationStatus === 'success' && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <CheckCircle className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800 dark:text-green-200">
                  {t('auth.verifyEmail.successHighlightTitle')}
                </h3>
                <p className="mt-2 text-sm text-green-700 dark:text-green-300">
                  {t('auth.verifyEmail.successHighlightDescription')}
                </p>
              </div>
            </div>
          </div>
        )}

        {verificationStatus === 'error' && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  {t('auth.verifyEmail.errorHighlightTitle')}
                </h3>
                <p className="mt-2 text-sm text-red-700 dark:text-red-300">
                  {t('auth.verifyEmail.errorHighlightDescription')}
                </p>
              </div>
            </div>
          </div>
        )}

        {verificationStatus === 'pending' && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <RefreshCw className="h-5 w-5 text-blue-400 animate-spin" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  {t('auth.verifyEmail.pendingHighlightTitle')}
                </h3>
                <p className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                  {t('auth.verifyEmail.pendingHighlightDescription')}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {verificationStatus !== 'pending' && (
            <Button onClick={() => navigate('/login')} className="w-full">
              {t('auth.verifyEmail.goToLogin')}
            </Button>
          )}

          <div className="text-center">
            <Link
              to="/login"
              className="text-sm font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
            >
              {t('auth.verifyEmail.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
